import React, { createContext, useCallback, useContext, useState, ReactNode } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, HelpCircle } from 'lucide-react';
import { Sheet, IconBadge } from '../components/UI';

export interface ConfirmOptions {
  title?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** 'danger' (Standard) fuer destruktive Aktionen (rotes Bestaetigen), 'default' fuer neutrale Rueckfragen. */
  tone?: 'default' | 'danger';
}

type ConfirmFn = (message: string, options?: ConfirmOptions) => Promise<boolean>;

const ConfirmDialogContext = createContext<ConfirmFn | undefined>(undefined);

/** Ersetzt `window.confirm` durch einen Dialog im Seiten-Design - `await confirm('...')` statt `window.confirm('...')`. */
export const useConfirm = (): ConfirmFn => {
  const ctx = useContext(ConfirmDialogContext);
  if (!ctx) {
    throw new Error('useConfirm must be used within a ConfirmDialogProvider');
  }
  return ctx;
};

interface PendingConfirm extends ConfirmOptions {
  message: string;
  resolve: (result: boolean) => void;
}

export const ConfirmDialogProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { t } = useTranslation();
  const [pending, setPending] = useState<PendingConfirm | null>(null);

  const confirm = useCallback<ConfirmFn>(
    (message, options) => new Promise<boolean>((resolve) => setPending({ message, resolve, ...options })),
    []
  );

  const settle = (result: boolean) => {
    pending?.resolve(result);
    setPending(null);
  };

  const isDanger = pending?.tone !== 'default';

  return (
    <ConfirmDialogContext.Provider value={confirm}>
      {children}
      <Sheet
        isOpen={!!pending}
        title={pending?.title ?? t('common:dialog.confirmTitle')}
        onClose={() => settle(false)}
        size="sm"
        footer={
          <div className="modal-actions">
            <button type="button" className="btn btn-ghost" onClick={() => settle(false)}>
              {pending?.cancelLabel ?? t('common:actions.cancel')}
            </button>
            <button
              type="button"
              className={clsx('btn', isDanger ? 'btn-danger' : 'btn-primary')}
              onClick={() => settle(true)}
            >
              {pending?.confirmLabel ?? t('common:actions.delete')}
            </button>
          </div>
        }
      >
        <div className="flex items-start gap-3">
          <IconBadge icon={isDanger ? <AlertTriangle size={18} /> : <HelpCircle size={18} />} tone={isDanger ? 'error' : 'primary'} />
          <p className="flex-1 pt-1.5 text-sm text-dark-200">{pending?.message}</p>
        </div>
      </Sheet>
    </ConfirmDialogContext.Provider>
  );
};
